// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {Gradient} from 'slices/SideGrid/components'

interface AnimatedBackgroundProps {
  /**
   * Offset top for the container (in pixels)
   * @default -50
   */
  offsetTop?: number
  /**
   * Gradient height (in pixels, default 500)
   * @default 500
   */
  gradientHeight?: number
  /**
   * Gradient start position (in percentage, default 50)
   * @default 50
   */
  gradientStart?: number
}

export const AnimatedBackground = ({
  offsetTop = -50,
  gradientHeight = 500,
  gradientStart = 50,
}: AnimatedBackgroundProps) => {
  // Animation timing matching the original
  const animationGap = 6.5
  const totalCycleTime = 12 * animationGap // 78s

  // Paths data for left side
  const leftPaths = [
    {d: 'M939.642 0V38.4658C939.642 525.324 544.965 920 58.1076 920H19.6417', id: 0},
    {d: 'M835.656 0C835.656 452.873 468.53 820 15.6564 820', id: 1},
    {d: 'M731.68 0C731.68 397.645 409.325 720 11.6798 720', id: 2},
    {d: 'M627.773 0C627.773 342.417 350.189 620 7.77258 620', id: 3},
    {d: 'M420 0C420 231.96 231.96 420 0.00012207 420', id: 4},
    {d: 'M524.345 0C524.345 287.188 291.533 520 4.3446 520', id: 5},
  ]

  // Paths data for right side
  const rightPaths = [
    {d: 'M2 0V38.4658C2 525.324 396.676 920 883.534 920H922', id: 6},
    {d: 'M105.985 0C105.985 452.873 473.112 820 925.985 820', id: 7},
    {d: 'M209.962 0C209.962 397.645 532.317 720 929.962 720', id: 8},
    {d: 'M313.869 0C313.869 342.417 591.453 620 933.869 620', id: 9},
    {d: 'M521.642 0C521.642 231.96 709.682 420 941.642 420', id: 10},
    {d: 'M417.297 0C417.297 287.188 650.109 520 937.297 520', id: 11},
  ]

  return (
    <div
      className="absolute inset-0 animate-fadeIn overflow-hidden delay-1000 duration-700"
      style={{top: offsetTop + 'px'}}
    >
      {/* Fixed width container that doesn't expand viewport */}
      <div className="absolute left-1/2 top-0 flex -translate-x-1/2">
        {/* Edge gradients - 40px fade to green-dark, positioned within the 1800px pattern */}
        <div className="absolute inset-y-0 left-0 z-10 w-[250px] bg-gradient-to-r from-green-dark to-transparent" />
        <div className="absolute inset-y-0 right-0 z-10 w-[250px] bg-gradient-to-l from-green-dark to-transparent" />
        <div className="absolute inset-x-0 bottom-0 z-10 h-[250px] bg-gradient-to-t from-green-dark to-transparent" />

        {/* Left Side Rectangles */}
        <svg
          width="900"
          height="1400"
          viewBox="0 0 942 922"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="shrink-0"
        >
          {leftPaths.map((path) => {
            const gradientId = `left-pulse-${path.id}`

            return (
              <g key={path.id}>
                {/* Base outline */}
                <path d={path.d} stroke="white" strokeWidth="2" strokeOpacity="0.15" />
                {/* Animated tracing stroke */}
                <path d={path.d} stroke={`url(#${gradientId})`} strokeWidth="4" strokeLinecap="round" />
              </g>
            )
          })}
          <defs>
            {leftPaths.map((path) => {
              const gradientId = `left-pulse-${path.id}`
              // Alternate between left and right sides: left paths animate at even positions (0, 2, 4, ...)
              const animationDelay = path.id * 2 * animationGap + 0.7

              return (
                <linearGradient key={gradientId} id={gradientId} gradientUnits="objectBoundingBox" x1="0.5" x2="0.5">
                  <stop offset="0" stopColor="white" stopOpacity="0" />
                  <stop offset="0.5" stopColor="#B9FF3F" stopOpacity="0.9" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />

                  <animate
                    attributeName="y1"
                    values="-0.6; -0.6; 1.2; 1.2"
                    keyTimes="0; 0; 0.0641; 1"
                    dur={`${totalCycleTime}s`}
                    begin={`${animationDelay}s`}
                    repeatCount="indefinite"
                  />

                  <animate
                    attributeName="y2"
                    values="0; 0; 1.8; 1.8"
                    keyTimes="0; 0; 0.0641; 1"
                    dur={`${totalCycleTime}s`}
                    begin={`${animationDelay}s`}
                    repeatCount="indefinite"
                  />
                </linearGradient>
              )
            })}
          </defs>
        </svg>

        {/* Right Side Rectangles */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="900"
          height="1400"
          viewBox="0 0 942 922"
          fill="none"
          className="shrink-0"
        >
          {rightPaths.map((path) => {
            const gradientId = `right-pulse-${path.id}`

            return (
              <g key={path.id}>
                {/* Base outline */}
                <path d={path.d} stroke="white" strokeWidth="2" strokeOpacity="0.15" />
                {/* Animated tracing stroke */}
                <path d={path.d} stroke={`url(#${gradientId})`} strokeWidth="4" strokeLinecap="round" />
              </g>
            )
          })}
          <defs>
            {rightPaths.map((path) => {
              const gradientId = `right-pulse-${path.id}`
              // Alternate between left and right sides: right paths animate at odd positions (1, 3, 5, ...)
              const pathIndex = path.id - 6
              const animationDelay = (pathIndex * 2 + 1) * animationGap + 0.7

              return (
                <linearGradient key={gradientId} id={gradientId} gradientUnits="objectBoundingBox" x1="0.5" x2="0.5">
                  <stop offset="0" stopColor="white" stopOpacity="0" />
                  <stop offset="0.5" stopColor="#B9FF3F" stopOpacity="0.9" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />

                  <animate
                    attributeName="y1"
                    values="-0.6; -0.6; 1.2; 1.2"
                    keyTimes="0; 0; 0.0641; 1"
                    dur={`${totalCycleTime}s`}
                    begin={`${animationDelay}s`}
                    repeatCount="indefinite"
                  />

                  <animate
                    attributeName="y2"
                    values="0; 0; 1.8; 1.8"
                    keyTimes="0; 0; 0.0641; 1"
                    dur={`${totalCycleTime}s`}
                    begin={`${animationDelay}s`}
                    repeatCount="indefinite"
                  />
                </linearGradient>
              )
            })}
          </defs>
        </svg>
      </div>

      {/* Gradient overlay for depth */}
      <Gradient color="0,36,20" gradientStart={gradientStart} height={gradientHeight} location="top" />
      <div className="absolute inset-0 bg-gradient-to-b from-green-dark/50 via-transparent to-green-dark/80" />
    </div>
  )
}
